/**
 * OS Keyboard Shortcuts — KURAL 114 OS pillar (system-wide bindings)
 * Window control:
 *   Cmd/Ctrl+W           : close active window
 *   Cmd/Ctrl+M           : minimize active window
 *   Cmd/Ctrl+H           : hide active window (minimize to dock)
 *   Cmd/Ctrl+Tab         : cycle to next window
 *   Cmd/Ctrl+Shift+Tab   : cycle to previous window
 *   F11 / Cmd+Ctrl+F     : fullscreen toggle
 *   Alt+Tab              : cycle (skip minimized)
 *   Alt+Shift+Tab        : cycle reverse (skip minimized)
 *   Ctrl+Up              : toggle Mission Control
 * OS overlays:
 *   Cmd/Ctrl+,           : open Settings window
 *   Cmd/Ctrl+/           : open Keyboard Shortcuts cheat sheet
 *   Cmd/Ctrl+Space       : open Spotlight
 *   Cmd/Ctrl+1..9        : focus N-th dock app
 *   Esc                  : dismiss top-most overlay (runai:dismiss-overlay)
 */
import { useEffect } from 'react'
import { useWindowManager } from '../components/os/WindowManager/WindowManagerContext'

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
}

function isEditableTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA') return true
  if (el.isContentEditable) return true
  return false
}

export function useOSKeyboardShortcuts() {
  const {
    windows, activeWindowId,
    closeWindow, minimizeWindow, maximizeWindow, restoreWindow, focusWindow,
  } = useWindowManager()

  useEffect(() => {
    const mac = isMac()

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip when typing in editable fields
      if (isEditableTarget(e.target)) return

      const mod = mac ? e.metaKey : e.ctrlKey
      const key = e.key.toLowerCase()

      // Cmd/Ctrl+W — close
      if (mod && key === 'w' && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        if (activeWindowId) closeWindow(activeWindowId)
        return
      }

      // Cmd/Ctrl+M — minimize
      if (mod && key === 'm' && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        if (activeWindowId) minimizeWindow(activeWindowId)
        return
      }

      // Cmd/Ctrl+H — hide (minimize to dock)
      if (mod && key === 'h' && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        if (activeWindowId) minimizeWindow(activeWindowId)
        return
      }

      // Cmd/Ctrl+Tab — cycle next
      if (mod && key === 'tab' && !e.shiftKey) {
        e.preventDefault()
        cycleWindow(1)
        return
      }

      // Cmd/Ctrl+Shift+Tab — cycle previous
      if (mod && key === 'tab' && e.shiftKey) {
        e.preventDefault()
        cycleWindow(-1)
        return
      }

      // F11 or Cmd+Ctrl+F — fullscreen toggle
      if (key === 'f11' || (mac && e.metaKey && e.ctrlKey && key === 'f')) {
        e.preventDefault()
        if (!activeWindowId) return
        const win = windows.find(w => w.id === activeWindowId)
        if (win?.maximized) {
          restoreWindow(activeWindowId)
        } else {
          maximizeWindow(activeWindowId)
        }
        return
      }

      // Alt+Tab — cycle next window (skip minimized)
      if (e.altKey && key === 'tab' && !e.shiftKey && !mod) {
        e.preventDefault()
        cycleWindowAlt(1)
        return
      }

      // Alt+Shift+Tab — cycle previous window (skip minimized)
      if (e.altKey && key === 'tab' && e.shiftKey && !mod) {
        e.preventDefault()
        cycleWindowAlt(-1)
        return
      }

      // Ctrl+Up — toggle Mission Control
      if (e.ctrlKey && e.key === 'ArrowUp' && !e.shiftKey && !e.altKey && !e.metaKey) {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('runai:mission-control-toggle'))
        return
      }

      // Cmd/Ctrl+, — open Settings window
      if (mod && e.key === ',' && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'os-settings' }))
        return
      }

      // Cmd/Ctrl+/ — open Keyboard Shortcuts cheat sheet
      if (mod && key === '/' && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'keyboard-shortcuts' }))
        return
      }

      // Cmd/Ctrl+Space — Spotlight
      if (mod && key === ' ' && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('runai:spotlight-open', { detail: '' }))
        return
      }

      // Cmd/Ctrl+1..9 — focus / open N-th dock app
      if (mod && /^[1-9]$/.test(e.key) && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        const idx = parseInt(e.key, 10) - 1
        window.dispatchEvent(new CustomEvent('runai:dock-launch', { detail: { index: idx } }))
        return
      }

      // Esc — dismiss top-most modal / close active spotlight / clear focus chip
      if (key === 'escape' && !mod && !e.shiftKey && !e.altKey) {
        // Don't preventDefault — let native behaviours (close <dialog>, blur input) still run.
        window.dispatchEvent(new CustomEvent('runai:dismiss-overlay'))
        return
      }
    }

    function cycleWindow(direction: 1 | -1) {
      const visible = windows.filter(w => !w.minimized)
      if (visible.length < 2) return
      const sorted = [...visible].sort((a, b) => b.zIndex - a.zIndex)
      const currentIdx = sorted.findIndex(w => w.id === activeWindowId)
      const nextIdx = (currentIdx + direction + sorted.length) % sorted.length
      focusWindow(sorted[nextIdx].id)
    }

    function cycleWindowAlt(direction: 1 | -1) {
      const visible = windows.filter(w => !w.minimized)
      if (visible.length < 2) return
      const sorted = [...visible].sort((a, b) => b.zIndex - a.zIndex)
      const currentIdx = sorted.findIndex(w => w.id === activeWindowId)
      const nextIdx = (currentIdx + direction + sorted.length) % sorted.length
      focusWindow(sorted[nextIdx].id)
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [windows, activeWindowId, closeWindow, minimizeWindow, maximizeWindow, restoreWindow, focusWindow])
}
